import { computed, onBeforeUnmount, ref } from 'vue';

/**
 * Performance metrics collection composable for WebGL/Three.js applications
 * Collects FPS, frame timing, GPU/CPU metrics, and WebGL statistics
 */
export const usePerformanceMetrics = () => {
    // FPS tracking
    const frameCount = ref(0);
    const lastFpsTime = ref(0);
    const currentFps = ref(0);
    const fpsHistory = ref([]);
    const FPS_HISTORY_SIZE = 60; // Keep 60 samples for averaging

    // Frame timing
    const frameTimes = ref([]);
    const FRAME_TIME_HISTORY_SIZE = 30;
    const lastFrameTime = ref(0);
    const frameJankHistory = ref([]);
    const JANK_THRESHOLD = 33; // 33ms = 30fps threshold

    // WebGL renderer info
    const webglStats = ref({
        drawCalls: 0,
        triangles: 0,
        programs: 0,
        geometries: 0,
        textures: 0,
        points: 0,
        lines: 0,
    });

    // Performance grade calculation
    const performanceGrade = computed(() => {
        const avgFps = averageFps.value;
        const avgFrameTime = averageFrameTime.value;
        const jankRatio = recentJankRatio.value;

        if (avgFps >= 55 && avgFrameTime < 16 && jankRatio < 0.05) return 'A';
        if (avgFps >= 45 && avgFrameTime < 20 && jankRatio < 0.1) return 'B';
        if (avgFps >= 30 && avgFrameTime < 33 && jankRatio < 0.2) return 'C';
        if (avgFps >= 20 && avgFrameTime < 50 && jankRatio < 0.3) return 'D';
        return 'F';
    });

    // Computed averages
    const averageFps = computed(() => {
        if (fpsHistory.value.length === 0) return 0;
        return fpsHistory.value.reduce((sum, fps) => sum + fps, 0) / fpsHistory.value.length;
    });

    const averageFrameTime = computed(() => {
        if (frameTimes.value.length === 0) return 0;
        return frameTimes.value.reduce((sum, time) => sum + time, 0) / frameTimes.value.length;
    });

    const minFrameTime = computed(() => {
        if (frameTimes.value.length === 0) return 0;
        return Math.min(...frameTimes.value);
    });

    const maxFrameTime = computed(() => {
        if (frameTimes.value.length === 0) return 0;
        return Math.max(...frameTimes.value);
    });

    const recentJankCount = computed(() => {
        return frameJankHistory.value.reduce((count, isJank) => count + (isJank ? 1 : 0), 0);
    });

    const recentJankRatio = computed(() => {
        if (frameJankHistory.value.length === 0) return 0;
        return recentJankCount.value / frameJankHistory.value.length;
    });

    /**
     * Update FPS calculation
     * @param {number} timestamp - Current timestamp
     */
    const updateFps = (timestamp) => {
        frameCount.value++;

        if (timestamp - lastFpsTime.value >= 1000) {
            const fps = frameCount.value;
            currentFps.value = fps;

            // Add to history and maintain size
            fpsHistory.value.push(fps);
            if (fpsHistory.value.length > FPS_HISTORY_SIZE) {
                fpsHistory.value.shift();
            }

            frameCount.value = 0;
            lastFpsTime.value = timestamp;
        }
    };

    /**
     * Update frame timing metrics
     * @param {number} timestamp - Current timestamp
     */
    const updateFrameTiming = (timestamp) => {
        if (lastFrameTime.value > 0) {
            const frameTime = timestamp - lastFrameTime.value;

            // Track frame times
            frameTimes.value.push(frameTime);
            if (frameTimes.value.length > FRAME_TIME_HISTORY_SIZE) {
                frameTimes.value.shift();
            }

            frameJankHistory.value.push(frameTime > JANK_THRESHOLD);
            if (frameJankHistory.value.length > FRAME_TIME_HISTORY_SIZE) {
                frameJankHistory.value.shift();
            }
        }
        lastFrameTime.value = timestamp;
    };

    /**
     * Main update function to be called each frame
     * @param {number} timestamp - Current timestamp
     * @param {Object} stats - Stats object from stats-gl
     * @param {Object} rendererInfo - Three.js renderer.info object
     */
    const updateMetrics = (timestamp, stats = null, rendererInfo = null) => {
        updateFps(timestamp);
        updateFrameTiming(timestamp);
    };

    /**
     * Get current performance snapshot
     * @returns {Object} Performance metrics object
     */
    const getMetricsSnapshot = () => {
        return {
            fps: {
                current: currentFps.value,
                average: averageFps.value,
                history: [...fpsHistory.value],
            },
            frameTiming: {
                current: frameTimes.value[frameTimes.value.length - 1] || 0,
                average: averageFrameTime.value,
                min: minFrameTime.value || 0,
                max: maxFrameTime.value || 0,
                jankCount: recentJankCount.value,
                jankRatio: recentJankRatio.value,
            },
            webgl: { ...webglStats.value },
        };
    };

    /**
     * Reset all metrics
     */
    const resetMetrics = () => {
        frameCount.value = 0;
        lastFpsTime.value = 0;
        currentFps.value = 0;
        fpsHistory.value = [];
        frameTimes.value = [];
        lastFrameTime.value = 0;
        frameJankHistory.value = [];
    };

    // Cleanup on unmount
    onBeforeUnmount(() => {
        resetMetrics();
    });

    return {
        // Reactive values
        currentFps,
        averageFps,
        averageFrameTime,
        performanceGrade,
        // Methods
        updateMetrics,
        getMetricsSnapshot,
        resetMetrics,
    };
};
